import MapMercureModel         from '@models/map/MapMercureModel';
import dayjs                   from 'dayjs';
import { EventSourcePolyfill } from 'event-source-polyfill';
import Proto                   from '@/proto/proto';
import { Buffer }              from 'buffer';
import _get                    from 'lodash/get';
import { getIdFromUrn }        from '@widesk-core/tools/identifier';
import { readModification }    from '@/TokamapEditor/stores/mercureModification';
import type { MercureEvent }     from '@/TokamapEditor/stores/EditorMercureListener';
import type { MercureEventData } from '@/TokamapEditor/stores/EditorMercureListener';

const services = window.APP_CONFIG?.services || [];
const serviceMercure = services.find(service => service.id === 'mercure');

type Subscription = {
	eventSource?: EventSourcePolyfill;
	authTimeout?: ReturnType<typeof setTimeout>;
};

/**
 * Écoute les modifications de TOUTES les cartes du projet, pour la seule "dernière modification"
 * affichée par `SaveStatusBanner` — ce message parle du projet, il doit donc bouger même quand la
 * carte modifiée n'est pas celle qu'on a sous les yeux.
 *
 * Un abonnement par carte, et non un seul abonnement à plusieurs topics : le jeton Mercure est
 * délivré par carte (`/maps/{id}/mercure`), il n'autoriserait pas les topics des autres. C'est donc
 * autant de connexions SSE que de cartes dans le projet — acceptable ici (une poignée de cartes,
 * aucun trafic tant que personne ne modifie), mais c'est le plafond de cette approche : le jour où
 * l'API sait délivrer un jeton à l'échelle du projet, tout ceci se ramène à une seule connexion.
 *
 * Volontairement distinct d'`EditorMercureListener`, qui lui APPLIQUE les évènements au plan :
 * appliquer à la carte ouverte les formes d'une autre carte serait évidemment faux. Ici on ne lit
 * que l'auteur et l'horodatage, sans jamais toucher à l'éditeur — et la carte ouverte est incluse
 * comme les autres, ce qui évite d'avoir à reconstruire les abonnements à chaque changement de carte
 * (le doublon avec `EditorMercureListener` ne coûte qu'un horodatage réécrit à l'identique).
 */
export default class ProjectMercureListener {
	private readonly _subscriptions = new Map<Urn, Subscription>();
	private readonly _onModification: (modification: { at: Date; byUserUrn?: Urn; mapUrn: Urn }) => void;

	public constructor(onModification: (modification: { at: Date; byUserUrn?: Urn; mapUrn: Urn }) => void) {
		this._onModification = onModification;
	}

	/**
	 * Aligne les abonnements sur la liste de cartes donnée : idempotent, pour pouvoir être rappelé
	 * librement (ouverture du projet, création ou suppression d'une carte en cours de session) sans
	 * jamais rouvrir une connexion déjà en place.
	 */
	public sync(mapUrns: Urn[]) {
		this._subscriptions.forEach((subscription, urn) => {
			if (mapUrns.includes(urn)) return;

			this._closeSubscription(subscription);
			this._subscriptions.delete(urn);
		});

		mapUrns
			.filter(urn => !this._subscriptions.has(urn))
			.forEach(urn => {
				const subscription: Subscription = {};
				this._subscriptions.set(urn, subscription);

				void this._connect(urn, subscription);
			});
	}

	public stop() {
		this._subscriptions.forEach(subscription => this._closeSubscription(subscription));
		this._subscriptions.clear();
	}

	private _closeSubscription(subscription: Subscription) {
		subscription.eventSource?.close();
		clearTimeout(subscription.authTimeout);
	}

	private async _connect(mapUrn: Urn, subscription: Subscription) {
		try {
			const token = new MapMercureModel();
			await token.setId(getIdFromUrn(mapUrn)).fetch();

			// La carte a pu être retirée (ou tout le listener arrêté) pendant la requête de jeton.
			if (this._subscriptions.get(mapUrn) !== subscription) return;

			subscription.eventSource?.close();

			const url = new URL(serviceMercure?.configurations.api_endpoint || '');
			url.searchParams.append('topic', mapUrn);

			const eventSource = new EventSourcePolyfill(url.toString().replace('/?', '?'), {
				headers: { 'Authorization': `Bearer ${token.jwt}` },
			});

			// La carte vient de l'abonnement, jamais du contenu du message : chaque connexion ne porte
			// qu'un topic, et selon le `kind` la carte se lirait dans un champ différent du proto
			// (`Feature.map`, ou l'id du `Map` lui-même).
			eventSource.onmessage = (e: MercureEvent) => this._onMessage(e, mapUrn);
			subscription.eventSource = eventSource;

			// Le renouvellement ROUVRE la connexion : l'en-tête `Authorization` d'un `EventSource` déjà
			// ouvert ne peut pas être remplacé, et une reconnexion automatique avec un jeton périmé
			// couperait l'abonnement en silence. Plancher d'une minute pour qu'un `exp` déjà passé (ou
			// une horloge locale en avance) ne parte pas en boucle de reconnexion.
			const timeRemaining = Math.max(token.exp.diff(dayjs(), 'second'), 60);
			subscription.authTimeout = setTimeout(() => void this._connect(mapUrn, subscription), timeRemaining * 1000);
		} catch (err) {
			// Une carte dont l'abonnement échoue ne doit pas empêcher les autres de fonctionner : on
			// perd sa remontée en direct, pas le reste (ni le calcul fait au chargement).
			console.error(`Abonnement Mercure "${mapUrn}"`, err);
		}
	}

	private _onMessage(e: MercureEvent, mapUrn: Urn) {
		try {
			const eventData = JSON.parse(e.data) as MercureEventData;
			const EventProto = _get(Proto, eventData.proto);

			if (!EventProto) return;

			const { at, byUserUrn } = readModification(EventProto.decode(Buffer.from(eventData.data, 'base64')));

			this._onModification({ at, byUserUrn, mapUrn });
		} catch (err) {
			console.error('Évènement Mercure du projet', err);
		}
	}
}
